import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import {
  ShieldAlert,
  XCircle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import { formatDateTime } from "@/lib/demo-data";
import { toast } from "sonner";

const SEVERITY_CONFIG: Record<string, { label: string; variant: "destructive" | "default" | "secondary" | "outline" }> = {
  CRITICAL: { label: "Critique", variant: "destructive" },
  HIGH: { label: "Haute", variant: "default" },
  MEDIUM: { label: "Moyenne", variant: "secondary" },
  LOW: { label: "Basse", variant: "outline" },
};

const PATTERN_LABELS: Record<string, string> = {
  MULTI_ACCOUNT: "Multi-compte",
  REFUND_ABUSE: "Abus remboursement",
  CHARGEBACK_VELOCITY: "Chargebacks rapides",
  VELOCITY_ATTACK: "Attaque velocite",
};

export default function FraudAlerts() {
  const [patternFilter, setPatternFilter] = useState<string>("all");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [dismissTarget, setDismissTarget] = useState<{ id: string; pattern: string } | null>(null);
  const [dismissReason, setDismissReason] = useState("");

  const utils = trpc.useUtils();

  const { data: alerts, isLoading, error } = trpc.admin.fraudAlerts.listActive.useQuery(
    undefined,
    { refetchInterval: 60_000 }
  );

  const dismissMutation = trpc.admin.fraudAlerts.dismiss.useMutation({
    onSuccess: () => {
      toast.success("Alerte classee");
      utils.admin.fraudAlerts.listActive.invalidate();
      setDismissTarget(null);
      setDismissReason("");
    },
    onError: (err) => {
      toast.error(err.message || "Erreur lors du classement");
    },
  });

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDismiss = () => {
    if (!dismissTarget) return;
    dismissMutation.mutate({
      id: dismissTarget.id,
      reason: dismissReason.trim() || undefined,
    });
  };

  const filtered = (alerts ?? []).filter((a: any) => {
    if (patternFilter !== "all" && a.pattern !== patternFilter) return false;
    if (severityFilter !== "all" && a.severity !== severityFilter) return false;
    return true;
  });

  if (isLoading) {
    return (
      <AdminLayout>
        <ListPageSkeleton />
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <EmptyState
          icon={<ShieldAlert className="h-8 w-8" />}
          title="Erreur de chargement"
          description={error.message}
          action={{ label: "Reessayer", onClick: () => utils.admin.fraudAlerts.listActive.invalidate() }}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Alertes Fraude</h1>
            <p className="text-muted-foreground text-sm">
              Patterns suspects detectes — {filtered.length} alerte{filtered.length !== 1 ? "s" : ""} active{filtered.length !== 1 ? "s" : ""}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => utils.admin.fraudAlerts.listActive.invalidate()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Rafraichir
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <Select value={patternFilter} onValueChange={setPatternFilter}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Pattern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les patterns</SelectItem>
              <SelectItem value="MULTI_ACCOUNT">Multi-compte</SelectItem>
              <SelectItem value="REFUND_ABUSE">Abus remboursement</SelectItem>
              <SelectItem value="CHARGEBACK_VELOCITY">Chargebacks rapides</SelectItem>
              <SelectItem value="VELOCITY_ATTACK">Attaque velocite</SelectItem>
            </SelectContent>
          </Select>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Severite" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes severites</SelectItem>
              <SelectItem value="CRITICAL">Critique</SelectItem>
              <SelectItem value="HIGH">Haute</SelectItem>
              <SelectItem value="MEDIUM">Moyenne</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <EmptyState
            icon={<ShieldAlert className="h-8 w-8" />}
            title="Aucune alerte active"
            description="Tous les patterns sont sains — aucune anomalie detectee."
          />
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" />
                    <TableHead>Severite</TableHead>
                    <TableHead>Pattern</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((alert: any) => {
                    const sev = SEVERITY_CONFIG[alert.severity] ?? SEVERITY_CONFIG.MEDIUM;
                    const isExpanded = expandedIds.has(alert.id);
                    return (
                      <>
                        <TableRow key={alert.id} className="cursor-pointer" onClick={() => toggleExpand(alert.id)}>
                          <TableCell>
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={sev.variant}>{sev.label}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">
                            {PATTERN_LABELS[alert.pattern] ?? alert.pattern}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDateTime(alert.createdAt)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDismissTarget({ id: alert.id, pattern: alert.pattern });
                              }}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Classer
                            </Button>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow key={`${alert.id}-detail`}>
                            <TableCell colSpan={5} className="bg-muted/30 p-4">
                              <pre className="text-xs overflow-auto max-h-48 whitespace-pre-wrap font-mono">
                                {JSON.stringify(alert.payload, null, 2)}
                              </pre>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dismiss confirmation dialog */}
      <Dialog open={!!dismissTarget} onOpenChange={(open) => { if (!open) setDismissTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Classer cette alerte ?</DialogTitle>
            <DialogDescription>
              Pattern : {dismissTarget ? (PATTERN_LABELS[dismissTarget.pattern] ?? dismissTarget.pattern) : ""}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Raison du classement (optionnel)"
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissTarget(null)}>
              Annuler
            </Button>
            <Button onClick={handleDismiss} disabled={dismissMutation.isPending}>
              {dismissMutation.isPending ? "Classement..." : "Confirmer le classement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
